// src/components/ui/ScrollContainer.js - Production version with horizontal support
import React, { useState, useEffect, useRef } from 'react';

/**
 * A reusable scroll container with hover-based scrollbar visibility
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to be scrolled
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.trackMargins] - Custom track margins { top, bottom, left, right }
 * @param {Object} [props.scrollbarColors] - Custom colors { normal, hover }
 * @param {boolean} [props.alwaysVisible] - Whether scrollbar should always be visible
 * @param {'vertical'|'horizontal'|'both'} [props.direction='vertical'] - Scroll direction
 */
const ScrollContainer = React.forwardRef(({
  children,
  className = '',
  trackMargins = {},
  scrollbarColors = {
    normal: '#C0C7CE', // app-gray-300
    hover: '#8F99A3'   // app-gray-400
  },
  alwaysVisible = false,
  direction = 'vertical',
  ...props
}, ref) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const styleId = useRef(`scroll-container-${Math.random().toString(36).substr(2, 9)}`);

  // Combine refs
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(containerRef.current);
      } else {
        ref.current = containerRef.current;
      }
    }
  }, [ref]);

  // Inject styles dynamically
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = styleId.current;
    
    const thumbColor = alwaysVisible || isHovered ? scrollbarColors.normal : 'transparent';
    const thumbHoverColor = scrollbarColors.hover;
    
    let styles = '';
    
    if (direction === 'vertical') {
      styles = `
        .${styleId.current}::-webkit-scrollbar {
          width: 6px ;
          height: 6px ;
        }
        
        .${styleId.current}::-webkit-scrollbar-track {
          background: transparent ;
          ${trackMargins.top ? `margin-top: ${trackMargins.top}px;` : ''}
          ${trackMargins.bottom ? `margin-bottom: ${trackMargins.bottom}px;` : ''}
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb {
          background: ${thumbColor} ;
          border-radius: 9999px ;
          transition: background-color 150ms ease-in-out;
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb:hover {
          background: ${thumbHoverColor} ;
        }
      `;
    } else if (direction === 'horizontal') {
      // Horizontal scrollbars: always visible with subtle styling
      styles = `
        .${styleId.current}::-webkit-scrollbar {
          width: 6px ;
          height: 8px ;
        }
        
        .${styleId.current}::-webkit-scrollbar-track {
          background: #F6F8FA ;
          ${trackMargins.left ? `margin-left: ${trackMargins.left}px;` : ''}
          ${trackMargins.right ? `margin-right: ${trackMargins.right}px;` : ''}
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb {
          background: #C0C7CE ;
          border-radius: 4px ;
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb:hover {
          background: #8F99A3 ;
        }
      `;
    } else if (direction === 'both') {
      styles = `
        .${styleId.current}::-webkit-scrollbar {
          width: 6px ;
          height: 8px ;
        }
        
        .${styleId.current}::-webkit-scrollbar-track {
          background: transparent ;
          ${trackMargins.top ? `margin-top: ${trackMargins.top}px;` : ''}
          ${trackMargins.bottom ? `margin-bottom: ${trackMargins.bottom}px;` : ''}
          ${trackMargins.left ? `margin-left: ${trackMargins.left}px;` : ''}
          ${trackMargins.right ? `margin-right: ${trackMargins.right}px;` : ''}
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb {
          background: ${thumbColor} ;
          border-radius: 9999px ;
          transition: background-color 150ms ease-in-out;
        }
        
        .${styleId.current}::-webkit-scrollbar-thumb:hover {
          background: ${thumbHoverColor} ;
        }
      `;
    }
    
    styleElement.textContent = styles;
    
    document.head.appendChild(styleElement);
    
    return () => {
      document.getElementById(styleId.current)?.remove();
    };
  }, [isHovered, alwaysVisible, scrollbarColors, trackMargins, direction]);

  // Determine overflow classes based on direction
  const overflowClasses = {
    vertical: 'overflow-y-auto overflow-x-hidden',
    horizontal: 'overflow-x-auto overflow-y-hidden',
    both: 'overflow-auto'
  }[direction];

  return (
    <div
      ref={containerRef}
      className={`${overflowClasses} ${styleId.current} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {children}
    </div>
  );
});

ScrollContainer.displayName = 'ScrollContainer';

export default ScrollContainer;